import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import { useSalesUnits, type SalesUnit } from "@/hooks/use-sales-units";
import { useAssignableUsers } from "@/hooks/use-users";
import {
  useUpsertSalesUnit,
  useDeleteSalesUnit,
  useAddSalesUnitMember,
  useRemoveSalesUnitMember,
} from "@/hooks/use-sales-units-admin";

export function SalesUnitsCard() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data: units = [], isLoading } = useSalesUnits(year);
  const upsertUnit = useUpsertSalesUnit();
  const deleteUnit = useDeleteSalesUnit();
  const [newUnit, setNewUnit] = useState<{ name: string; bf_target_mnok: string } | null>(
    null,
  );

  async function createUnit() {
    if (!newUnit?.name.trim()) {
      toast.error("Navn er påkrevd");
      return;
    }
    try {
      await upsertUnit.mutateAsync({
        name: newUnit.name.trim(),
        year,
        bf_target_mnok: Number(newUnit.bf_target_mnok) || 0,
      });
      setNewUnit(null);
      toast.success("Selger opprettet");
    } catch (err) {
      toast.error(`Kunne ikke opprette: ${(err as Error).message}`);
    }
  }

  async function removeUnit(id: string, name: string) {
    if (!window.confirm(`Slett "${name}"?`)) return;
    try {
      await deleteUnit.mutateAsync(id);
      toast.success("Selger slettet");
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SrIcon name="users" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Selgere</h2>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => setNewUnit({ name: "", bf_target_mnok: "" })}
              disabled={!!newUnit}
            >
              <SrIcon name="plus" className="w-3.5 h-3.5 mr-1" />
              Ny selger
            </Button>
          </div>
        </div>

        {newUnit && (
          <div className="rounded-md border bg-muted/30 p-3 mb-3 grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2">
            <Input
              placeholder="Navn (f.eks. Sigurd & Christer)"
              value={newUnit.name}
              onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
            />
            <Input
              type="number"
              step="0.1"
              placeholder="GEEK-mål MNOK"
              value={newUnit.bf_target_mnok}
              onChange={(e) => setNewUnit({ ...newUnit, bf_target_mnok: e.target.value })}
            />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setNewUnit(null)}>
                Avbryt
              </Button>
              <Button size="sm" onClick={createUnit} disabled={upsertUnit.isPending}>
                Opprett
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <SrIcon
              name="loader2"
              className="w-5 h-5 animate-spin text-muted-foreground"
            />
          </div>
        ) : units.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Ingen selgere for {year}. Opprett en med "Ny selger"-knappen.
          </p>
        ) : (
          <Accordion type="multiple" className="border rounded-md">
            {units.map((unit) => (
              <SalesUnitRow
                key={unit.id}
                unit={unit}
                onRemove={() => removeUnit(unit.id, unit.name)}
              />
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

function SalesUnitRow({ unit, onRemove }: { unit: SalesUnit; onRemove: () => void }) {
  const upsertUnit = useUpsertSalesUnit();
  const addMember = useAddSalesUnitMember();
  const removeMember = useRemoveSalesUnitMember();
  const { data: allUsers = [] } = useAssignableUsers();
  const [name, setName] = useState(unit.name);
  const [bf, setBf] = useState(String(unit.bf_target_mnok ?? 0));
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);

  const dirty = name !== unit.name || Number(bf) !== Number(unit.bf_target_mnok ?? 0);
  const memberUserIds = new Set((unit.members ?? []).map((m) => m.user_id));
  const availableUsers = allUsers.filter((u) => !memberUserIds.has(u.id));

  async function save() {
    try {
      await upsertUnit.mutateAsync({
        id: unit.id,
        name: name.trim(),
        year: unit.year,
        bf_target_mnok: Number(bf) || 0,
      });
      toast.success("Lagret");
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  async function addMemberClick(userId: string) {
    try {
      await addMember.mutateAsync({ sales_unit_id: unit.id, user_id: userId });
      setMemberPickerOpen(false);
    } catch (err) {
      toast.error(`Kunne ikke legge til: ${(err as Error).message}`);
    }
  }

  async function removeMemberClick(userId: string) {
    try {
      await removeMember.mutateAsync({ sales_unit_id: unit.id, user_id: userId });
    } catch (err) {
      toast.error(`Kunne ikke fjerne: ${(err as Error).message}`);
    }
  }

  return (
    <AccordionItem value={unit.id}>
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <div className="flex items-center gap-3 flex-1">
          <span className="font-medium text-sm">{unit.name}</span>
          <span className="text-xs text-muted-foreground">
            {unit.bf_target_mnok ?? 0} MNOK
          </span>
          <span className="text-xs text-muted-foreground ml-auto mr-3">
            {(unit.members ?? []).length} medlem(mer)
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Navn</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">GEEK-mål MNOK</Label>
              <Input
                type="number"
                step="0.1"
                value={bf}
                onChange={(e) => setBf(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || upsertUnit.isPending}
              className="h-8"
            >
              Lagre
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemove}
              className="h-8 text-destructive hover:text-destructive"
            >
              <SrIcon name="trash2" className="w-3.5 h-3.5" />
            </Button>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Medlemmer</Label>
            <div className="flex flex-wrap gap-2">
              {(unit.members ?? []).map((m) => (
                <span
                  key={m.user_id}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs border bg-muted/40"
                >
                  {m.user?.full_name ?? m.user_id.slice(0, 6)}
                  <button
                    type="button"
                    onClick={() => removeMemberClick(m.user_id)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Fjern medlem"
                  >
                    <SrIcon name="x" className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <Popover open={memberPickerOpen} onOpenChange={setMemberPickerOpen}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs">
                    <SrIcon name="plus" className="w-3 h-3 mr-1" />
                    Legg til
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Søk ansatte…" />
                    <CommandList>
                      <CommandEmpty>Ingen flere ansatte tilgjengelig</CommandEmpty>
                      <CommandGroup>
                        {availableUsers.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={u.full_name ?? u.email ?? u.id}
                            onSelect={() => addMemberClick(u.id)}
                            className="text-xs"
                          >
                            {u.full_name ?? u.email ?? u.id}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
